/**
 * WorkerPicker — the one employee field in the app.
 *
 * A `<Select>` with the whole company inside it cannot be used past a dozen
 * names: there is nothing to type into, and on a half-width field the popup
 * opens over the form it belongs to. This is a combobox instead — type a
 * fragment of any part of the name and the list narrows.
 *
 * The search is built for Albanian names:
 *   • diacritic-blind — "besnik" / "cela" find "Bësnik Çela";
 *   • word-based — every typed fragment must start some part of the name, so
 *     the surname alone ("sejdiu") or two fragments ("fis se") both work;
 *   • plain substring as the fallback ("rasniqi" → "Arben Krasniqi").
 *
 * Worker objects arrive in two shapes across the app — `{ id, name }` from the
 * inbox and `{ Id, FullName | FirstName + LastName }` from /api/selectworker —
 * so both are normalised here rather than at every call site.
 */
import React, { useMemo } from 'react';
import { Autocomplete, TextField, Box, Avatar, InputAdornment, Divider } from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import { isActiveWorker } from '../../utils/workerStatus';
import { contactEmailOf } from '../../services/workerNormalizer';

export const workerId = (w) => w?.Id ?? w?.id ?? '';
export const workerName = (w) => (
  w?.FullName || w?.name || `${w?.FirstName ?? w?.first_name ?? ''} ${w?.LastName ?? w?.last_name ?? ''}`.trim()
);

export const normalize = (s) => String(s || '')
  .toLocaleLowerCase('sq').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * Kërkimi prek edhe adresën, jo vetëm emrin.
 *
 * Me emra të përsëritur — dy «Fisnik Sejdiu» te e njëjta listë — emri nuk
 * mjafton për të zgjedhur të duhurin, dhe HR-ja e njeh personin nga adresa e
 * tij. Email-i copëtohet te pjesët e veta (`fisnik`, `sejdiu`, `ata-bk`, `com`),
 * ndaj gjendet edhe me një fragment të vetëm, edhe duke shkruar tërë adresën.
 */
const emailWords = (email) => normalize(email).split(/[@._\-+]+/).filter(Boolean);

export const filterWorkers = (options, { inputValue }) => {
  const q = normalize(inputValue).trim();
  if (!q) return options;
  const terms = q.split(/\s+/);
  return options.filter((o) => {
    if (o.all) return false;               // "Të gjithë" is not a search result
    const label = normalize(o.label);
    const email = normalize(o.email || '');
    const words = [...label.split(/\s+/), ...emailWords(o.email)];
    return terms.every((t) => words.some((w) => w.startsWith(t)) || label.includes(t) || email.includes(t));
  });
};

export const initials = (name) => String(name || '').trim().split(/\s+/).slice(0, 2)
  .map((w) => w[0] || '').join('').toLocaleUpperCase('sq');

export default function WorkerPicker({
  workers = [],
  value,
  onChange,
  includeInactive = false,    // ekranet e historikut e ndezin me qëllim
  label = 'Punonjësi',
  required = false,
  error = false,
  helperText,
  placeholder = 'Shkruaj emrin ose email-in…',
  emptyLabel,                 // e.g. "Të gjithë" — turns the field into a filter
  accent = '#3D8FA3',
  size = 'small',
  fullWidth = true,
  disabled = false,
  onBlur,
  sx,
}) {
  // Të larguarit nuk zgjidhen: fusha shërben për të caktuar punë, jo për të
  // lexuar historikun. I zgjedhuri MBETET gjithsesi — po e hoqëm, një rekord i
  // vjetër i një punëtori të larguar do të hapej me fushën bosh dhe ruajtja e
  // radhës do ta fshinte lidhjen pa e kërkuar askush.
  const options = useMemo(() => {
    const list = workers
      .filter((w) => includeInactive || isActiveWorker(w) || String(workerId(w)) === String(value))
      .map((w) => ({ value: workerId(w), label: workerName(w), email: contactEmailOf(w) }));
    return emptyLabel ? [{ value: '', label: emptyLabel, all: true }, ...list] : list;
  }, [workers, emptyLabel, includeInactive, value]);

  const selected = (value === '' || value == null)
    ? null
    : options.find((o) => !o.all && String(o.value) === String(value)) || null;

  return (
    <Autocomplete
      options={options}
      value={selected}
      onChange={(_, o) => onChange?.(o && !o.all ? o.value : '')}
      onBlur={onBlur}
      disabled={disabled}
      filterOptions={filterWorkers}
      getOptionLabel={(o) => o?.label || ''}
      isOptionEqualToValue={(o, v) => String(o.value) === String(v?.value)}
      size={size} fullWidth={fullWidth} sx={sx}
      openOnFocus autoHighlight autoComplete blurOnSelect
      noOptionsText="Asnjë punonjës nuk përputhet"
      // Tetë rreshta prej 36px, plus 4px frymë poshtë. Ishte 240px me rreshta pa
      // lartësi të caktuar — mjaftonte për gjashtë emra dhe rreshti i fundit
      // mbetej i prerë. Lartësia e rreshtit është tani e shprehur (`minHeight:
      // 36`), ndaj sa emra nxë lista është vendim, jo rastësi.
      ListboxProps={{ style: { maxHeight: 292, paddingTop: 0, paddingBottom: 4 } }}
      renderOption={(props, o) => (
        <React.Fragment key={String(o.value)}>
          <Box component="li" {...props}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 13.5, minHeight: 36, py: 0.5 }}>
            {/* "Të gjithë" është zgjedhje e vërtetë, jo vend-mbajtës. Më parë
                ishte tekst i pjerrët gri pranë një katrori bosh — lexohej si
                rresht i shkyçur, dhe njeriu s'e provonte fare. Tani ka shenjën
                e vet dhe peshën e një veprimi. */}
            {o.all ? (
              <Box sx={{
                width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: 'action.hover', color: 'text.secondary',
              }}>
                <GroupsRoundedIcon sx={{ fontSize: 15 }} />
              </Box>
            ) : (
              <Avatar sx={{
                width: 24, height: 24, fontSize: '0.62rem', fontWeight: 800, flexShrink: 0,
                bgcolor: alpha(accent, 0.14), color: accent,
              }}>
                {initials(o.label)}
              </Avatar>
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box component="span" sx={{ display: 'block', fontWeight: o.all ? 700 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {o.label}
              </Box>
              {/* Pa këtë, kërkimi me email do të gjente një rresht ku adresa
                  s'duket kund — dhe dy emra të njëjtë do të mbeteshin po aq të
                  padallueshëm sa më parë. */}
              {!o.all && o.email && (
                <Box component="span" sx={{ display: 'block', fontSize: 11.5, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {o.email}
                </Box>
              )}
            </Box>
          </Box>
          {/* Ndarësja e veçon veprimin nga lista e njerëzve — pa të, "Të gjithë"
              duket si emri i parë në listë. */}
          {o.all && <Divider component="li" sx={{ my: 0.5 }} />}
        </React.Fragment>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label} required={required} error={error} helperText={helperText}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            startAdornment: selected ? params.InputProps.startAdornment : (
              <InputAdornment position="start" sx={{ ml: 0.25, mr: -0.25 }}>
                <SearchRoundedIcon sx={{ fontSize: 17, color: '#94A3B8' }} />
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
}
